import {
  DEFAULT_PANELS,
  PANEL_HEADER_HEIGHT,
  WORKSPACE_STORAGE_KEY,
  clampFloatForRender,
  dockPanelAdjacent,
  findSnap,
  fitBottomLaneHeights,
  fitDockStackSizes,
  fitSideLaneWidths,
  normaliseWorkspace,
} from './panel-layout.js';

const RESIZE_EDGES = ['n', 'e', 's', 'w', 'ne', 'nw', 'se', 'sw'];

function copy(value) {
  return JSON.parse(JSON.stringify(value));
}

function read(storage) {
  try {
    return JSON.parse(storage?.getItem(WORKSPACE_STORAGE_KEY) ?? 'null');
  } catch {
    return null;
  }
}

function rectOf(element) {
  const rect = element.getBoundingClientRect();
  return { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
}

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), Math.max(minimum, maximum));
}

/** Normalize panels registered outside DEFAULT_PANELS against current chrome. */
function normaliseRegisteredPanel(value, fallback, viewport) {
  const source = value && typeof value === 'object' ? value : fallback;
  const modes = new Set(['dock', 'float', 'closed']);
  const edges = new Set(['left', 'right', 'bottom']);
  const mode = modes.has(source.mode) ? source.mode : fallback.mode;
  const number = (candidate, alternative) => Number.isFinite(candidate) ? candidate : alternative;
  const width = clamp(number(source.width, fallback.width), 48, viewport.width);
  const height = clamp(number(source.height, fallback.height), PANEL_HEADER_HEIGHT, viewport.height);

  if (mode === 'dock') {
    return {
      mode,
      edge: edges.has(source.edge) ? source.edge : (fallback.edge ?? 'left'),
      lane: Math.max(0, Math.trunc(number(source.lane, fallback.lane ?? 0))),
      order: Math.max(0, Math.trunc(number(source.order, fallback.order ?? 0))),
      width,
      height,
    };
  }

  const x = clamp(number(source.x, fallback.x ?? viewport.x), viewport.x, viewport.x + viewport.width - width);
  const y = clamp(number(source.y, fallback.y ?? viewport.y), viewport.y, viewport.y + viewport.height - height);
  if (mode === 'float') return { mode, x, y, width, height };
  return {
    mode,
    openMode: source.openMode === 'dock' ? 'dock' : 'float',
    x,
    y,
    width,
    height,
    ...(edges.has(source.edge) ? {
      edge: source.edge,
      lane: Math.max(0, Math.trunc(number(source.lane, 0))),
      order: Math.max(0, Math.trunc(number(source.order, 0))),
    } : {}),
  };
}

function sideMetrics(items, fittedWidths = null) {
  const lanes = new Map();
  for (const item of items) {
    const lane = item.state.lane ?? 0;
    if (!lanes.has(lane)) lanes.set(lane, []);
    lanes.get(lane).push(item);
  }
  const boxes = new Map();
  let total = 0;
  let minimumHeight = 0;
  const rows = [...lanes].sort(([a], [b]) => a - b);
  const laneWidths = rows.map(([, stack]) => Math.max(...stack.map((item) => item.state.width)));
  rows.forEach(([, stack], laneIndex) => {
    stack.sort((a, b) => (a.state.order ?? 0) - (b.state.order ?? 0));
    const width = fittedWidths?.[laneIndex] ?? laneWidths[laneIndex];
    minimumHeight = Math.max(minimumHeight, stack.length * PANEL_HEADER_HEIGHT);
    const desiredSizes = stack.map((item) => item.state.height);
    stack.forEach((item, index) => boxes.set(item.id, {
      offset: total, width, index, count: stack.length, desiredSizes,
    }));
    total += width;
  });
  return { boxes, total, minimumHeight, laneWidths };
}

function bottomMetrics(items, maxHeight, requiredUpperHeight = 1) {
  const lanes = new Map();
  for (const item of items) {
    const lane = item.state.lane ?? 0;
    if (!lanes.has(lane)) lanes.set(lane, []);
    lanes.get(lane).push(item);
  }
  const rows = [...lanes].sort(([a], [b]) => a - b);
  rows.forEach(([, row]) => row.sort((a, b) => (a.state.order ?? 0) - (b.state.order ?? 0)));
  const desiredHeights = rows.map(([, row]) => Math.max(...row.map((item) => item.state.height)));
  const fitted = fitBottomLaneHeights(
    desiredHeights,
    maxHeight,
    PANEL_HEADER_HEIGHT,
    PANEL_HEADER_HEIGHT,
    requiredUpperHeight,
  );
  const boxes = new Map();
  if (fitted.mode === 'viewport-only') return { boxes, total: 0, mode: fitted.mode };
  if (fitted.mode === 'row' || fitted.mode === 'combined-row') {
    const panels = rows.flatMap(([, row]) => row);
    panels.forEach((item, index) => boxes.set(item.id, {
      offset: 0, height: fitted.heights[0], index, count: panels.length,
      desiredSizes: panels.map((panel) => panel.state.width),
    }));
    return { boxes, total: fitted.heights[0], mode: fitted.mode };
  }
  let total = 0;
  rows.forEach(([, row], rowIndex) => {
    const height = fitted.heights[rowIndex];
    row.forEach((item, index) => boxes.set(item.id, {
      offset: total, height, index, count: row.length,
      desiredSizes: row.map((panel) => panel.state.width),
    }));
    total += height;
  });
  return { boxes, total, mode: fitted.mode };
}

export function createPanelManager({ root, viewport, storage, breakpoint = 760 }) {
  const frame = root.querySelector('#viewport-frame');
  const preview = root.querySelector('#panel-snap-preview') ?? document.createElement('div');
  if (!preview.id) {
    preview.id = 'panel-snap-preview';
    preview.setAttribute('aria-hidden', 'true');
    root.append(preview);
  }
  preview.setAttribute('role', 'status');
  preview.setAttribute('aria-label', 'Panel snap preview');
  preview.setAttribute('aria-hidden', 'true');

  const panels = new Map();
  const lastOpen = new Map();
  const cleanup = [];
  const activeGestures = new Set();
  const savedWorkspace = read(storage);
  let desktopSource = savedWorkspace ? copy(savedWorkspace) : null;
  let workspace = normaliseWorkspace(savedWorkspace, usableBounds());
  let narrow = (root.clientWidth || window.innerWidth) < breakpoint;
  let restoreDesktopOnWide = narrow;
  let sheetOpen = 'outliner';
  let narrowToolsOpen = workspace.panels.tools?.mode !== 'closed';
  let z = 30;
  let pointer = { x: 0, y: 30 };

  function usableBounds() {
    const menuHeight = root.querySelector('#menubar')?.offsetHeight || 30;
    const statusHeight = root.querySelector('#statusbar')?.offsetHeight || 34;
    return {
      x: 0,
      y: menuHeight,
      width: Math.max(1, root.clientWidth || window.innerWidth),
      height: Math.max(1, (root.clientHeight || window.innerHeight) - menuHeight - statusHeight),
    };
  }

  function persist(force = false) {
    if (narrow && !force) return;
    try {
      storage?.setItem(WORKSPACE_STORAGE_KEY, JSON.stringify(workspace));
    } catch {
      // Workspace persistence is best-effort; an unavailable localStorage
      // must not make the editor unusable.
    }
  }

  function panelState(id) {
    return workspace.panels[id] ?? panels.get(id)?.defaults ?? DEFAULT_PANELS[id];
  }

  function emit(id, visibleMode) {
    panels.get(id)?.element.dispatchEvent(
      new CustomEvent('panelchange', {
        detail: { id, mode: visibleMode, layout: copy(panelState(id)) },
      }),
    );
  }

  function setBox(element, { x, y, width, height }) {
    element.style.left = `${Math.round(x)}px`;
    element.style.top = `${Math.round(y)}px`;
    element.style.width = `${Math.round(width)}px`;
    element.style.height = `${Math.round(height)}px`;
  }

  function applyDesktop() {
    root.classList.remove('workspace-narrow');
    const usable = usableBounds();
    const docked = [...panels.entries()]
      .map(([id, registration]) => ({ id, ...registration, state: panelState(id) }))
      .filter(({ state }) => state?.mode === 'dock');
    const left = docked.filter(({ state }) => state.edge === 'left').sort((a, b) => a.state.lane - b.state.lane);
    const right = docked.filter(({ state }) => state.edge === 'right').sort((a, b) => a.state.lane - b.state.lane);
    const bottom = docked.filter(({ state }) => state.edge === 'bottom').sort((a, b) => a.state.lane - b.state.lane);
    const naturalLeftMetrics = sideMetrics(left);
    const naturalRightMetrics = sideMetrics(right);
    const horizontalFit = fitSideLaneWidths(
      naturalLeftMetrics.laneWidths,
      naturalRightMetrics.laneWidths,
      usable.width,
    );
    const leftMetrics = sideMetrics(left, horizontalFit.left);
    const rightMetrics = sideMetrics(right, horizontalFit.right);
    const sidePanelMinimum = Math.max(leftMetrics.minimumHeight, rightMetrics.minimumHeight, 1);
    const lowerMetrics = bottomMetrics(bottom, usable.height, sidePanelMinimum);
    const ultraShort = lowerMetrics.mode === 'combined-row';
    const viewportOnly = lowerMetrics.mode === 'viewport-only';
    const horizontalViewportOnly = !ultraShort && !viewportOnly && horizontalFit.mode === 'viewport-only';
    const combinedIndexes = new Map(docked.map(({ id }, index) => [id, index]));
    const leftUsed = ultraShort || viewportOnly || horizontalViewportOnly ? 0 : leftMetrics.total;
    const rightUsed = ultraShort || viewportOnly || horizontalViewportOnly ? 0 : rightMetrics.total;
    const bottomHeight = lowerMetrics.total;
    root.classList.toggle('workspace-ultra-short', ultraShort);
    root.classList.toggle('workspace-viewport-only', viewportOnly);

    for (const [id, { element }] of panels) {
      const state = panelState(id);
      const sideSuppressed = horizontalViewportOnly && ['left', 'right'].includes(state.edge);
      element.dataset.panelMode = state.mode;
      element.dataset.panelSuppressed = String(state.mode === 'dock' && (viewportOnly || sideSuppressed));
      element.classList.remove('sheet');
      if (state.mode === 'closed') {
        emit(id, 'closed');
        continue;
      }
      if (state.mode === 'float') {
        setBox(element, clampFloatForRender(state, usable));
        element.style.zIndex = element.style.zIndex || String(++z);
      } else if (viewportOnly || sideSuppressed) {
        emit(id, state.mode);
        continue;
      } else if (ultraShort) {
        const index = combinedIndexes.get(id);
        const panelWidth = usable.width / docked.length;
        setBox(element, {
          x: usable.x + panelWidth * index,
          y: usable.y + usable.height - bottomHeight,
          width: panelWidth,
          height: bottomHeight,
        });
      } else if (state.edge === 'left') {
        const box = leftMetrics.boxes.get(id);
        const stackHeights = fitDockStackSizes(box.desiredSizes, usable.height - bottomHeight);
        setBox(element, {
          x: usable.x + box.offset,
          y: usable.y + stackHeights.slice(0, box.index).reduce((sum, height) => sum + height, 0),
          width: box.width,
          height: stackHeights[box.index],
        });
      } else if (state.edge === 'right') {
        const box = rightMetrics.boxes.get(id);
        const stackHeights = fitDockStackSizes(box.desiredSizes, usable.height - bottomHeight);
        setBox(element, {
          x: usable.x + usable.width - box.offset - box.width,
          y: usable.y + stackHeights.slice(0, box.index).reduce((sum, height) => sum + height, 0),
          width: box.width,
          height: stackHeights[box.index],
        });
      } else {
        const box = lowerMetrics.boxes.get(id);
        const availableWidth = Math.max(1, usable.width - leftUsed - rightUsed);
        const panelWidths = fitDockStackSizes(box.desiredSizes, availableWidth, 48);
        setBox(element, {
          x: usable.x + leftUsed + panelWidths.slice(0, box.index).reduce((sum, width) => sum + width, 0),
          y: usable.y + usable.height - box.offset - box.height,
          width: panelWidths[box.index],
          height: box.height,
        });
      }
      emit(id, state.mode);
    }

    frame.style.left = `${leftUsed}px`;
    frame.style.right = `${rightUsed}px`;
    frame.style.top = `${usable.y}px`;
    frame.style.bottom = `${(root.clientHeight || window.innerHeight) - usable.y - usable.height + bottomHeight}px`;
  }

  function applyNarrow() {
    root.classList.add('workspace-narrow');
    root.classList.remove('workspace-ultra-short');
    root.classList.remove('workspace-viewport-only');
    const usable = usableBounds();
    for (const [id, { element }] of panels) {
      element.dataset.panelSuppressed = 'false';
      const isTools = id === 'tools';
      const open = isTools ? narrowToolsOpen : id === sheetOpen;
      element.dataset.panelMode = open ? (isTools ? 'dock' : 'sheet') : 'closed';
      element.classList.toggle('sheet', open && !isTools);
      if (isTools && open) {
        setBox(element, { x: 0, y: usable.y, width: Math.min(48, usable.width), height: usable.height });
      } else if (open) {
        setBox(element, {
          x: Math.min(48, usable.width),
          y: usable.y,
          width: Math.max(1, usable.width - Math.min(48, usable.width)),
          height: Math.min(usable.height, Math.max(280, usable.height * 0.72)),
        });
      }
      emit(id, open ? (isTools ? 'dock' : 'sheet') : 'closed');
    }
    frame.style.left = narrowToolsOpen ? '48px' : '0px';
    frame.style.right = '0px';
    frame.style.top = `${usable.y}px`;
    frame.style.bottom = `${(root.clientHeight || window.innerHeight) - usable.y - usable.height}px`;
  }

  function refreshResponsiveState() {
    const nextNarrow = (root.clientWidth || window.innerWidth) < breakpoint;
    if (nextNarrow && !narrow) narrowToolsOpen = panelState('tools')?.mode !== 'closed';
    if (!nextNarrow && narrow && restoreDesktopOnWide) {
      const bounds = usableBounds();
      const restored = normaliseWorkspace(desktopSource, bounds);
      for (const [id, registration] of panels) {
        if (!DEFAULT_PANELS[id]) {
          restored.panels[id] = normaliseRegisteredPanel(
            desktopSource?.panels?.[id], registration.defaults, bounds,
          );
        }
      }
      workspace = restored;
      restoreDesktopOnWide = false;
    }
    narrow = nextNarrow;
  }

  function layout() {
    refreshResponsiveState();
    if (narrow) applyNarrow();
    else applyDesktop();
    viewport?.resize?.();
    return copy(workspace);
  }

  function saveAndLayout() {
    const registeredExtras = Object.fromEntries(
      [...panels.keys()]
        .filter((id) => !DEFAULT_PANELS[id])
        .map((id) => [id, normaliseRegisteredPanel(
          workspace.panels[id], panels.get(id).defaults, usableBounds(),
        )]),
    );
    workspace = normaliseWorkspace(workspace, usableBounds());
    Object.assign(workspace.panels, registeredExtras);
    if (!narrow) desktopSource = copy(workspace);
    persist();
    layout();
  }

  function raise(id) {
    const registration = panels.get(id);
    if (registration && panelState(id)?.mode === 'float') registration.element.style.zIndex = String(++z);
  }

  function previewBox(snap, dragged) {
    const usable = usableBounds();
    if (snap.kind === 'edge') {
      if (snap.edge === 'left') return { x: usable.x, y: usable.y, width: dragged.width, height: usable.height };
      if (snap.edge === 'right') return { x: usable.x + usable.width - dragged.width, y: usable.y, width: dragged.width, height: usable.height };
      return { x: usable.x, y: usable.y + usable.height - dragged.height, width: usable.width, height: dragged.height };
    }
    const target = panels.get(snap.id)?.element;
    if (!target) return dragged;
    const candidate = rectOf(target);
    if (snap.side === 'left') return { ...dragged, x: candidate.x - dragged.width, y: candidate.y };
    if (snap.side === 'right') return { ...dragged, x: candidate.x + candidate.width, y: candidate.y };
    if (snap.side === 'top') return { ...dragged, x: candidate.x, y: candidate.y - dragged.height };
    return { ...dragged, x: candidate.x, y: candidate.y + candidate.height };
  }

  function showPreview(snap, dragged) {
    if (!snap) {
      preview.dataset.open = 'false';
      preview.setAttribute('aria-hidden', 'true');
      return;
    }
    setBox(preview, previewBox(snap, dragged));
    preview.dataset.open = 'true';
    preview.setAttribute('aria-hidden', 'false');
  }

  function commitSnap(id, snap, dragged) {
    if (!snap) {
      workspace.panels[id] = { ...panelState(id), ...dragged, mode: 'float' };
      return;
    }
    if (snap.kind === 'edge') {
      const lanes = [...panels.keys()]
        .filter((candidateId) => candidateId !== id)
        .map(panelState)
        .filter((state) => state?.mode === 'dock' && state.edge === snap.edge)
        .map((state) => state.lane ?? 0);
      workspace.panels[id] = {
        mode: 'dock', edge: snap.edge, lane: lanes.length ? Math.max(...lanes) + 1 : 0,
        order: 0, width: dragged.width, height: dragged.height,
      };
      return;
    }
    const targetState = panelState(snap.id);
    if (targetState?.mode === 'dock') {
      workspace.panels = dockPanelAdjacent(
        workspace.panels,
        id,
        snap.id,
        snap.side,
        { width: dragged.width, height: dragged.height },
      );
    } else {
      workspace.panels[id] = { ...panelState(id), ...previewBox(snap, dragged), mode: 'float' };
    }
  }

  function keyboardAdjust(id, key, resizing) {
    const current = panelState(id);
    if (narrow || current?.mode === 'closed') return;
    const element = panels.get(id).element;
    const visible = rectOf(element);
    const direction = {
      ArrowLeft: [-10, 0], ArrowRight: [10, 0], ArrowUp: [0, -10], ArrowDown: [0, 10],
    }[key];
    if (!direction) return;

    if (resizing) {
      workspace.panels[id] = {
        ...current,
        width: Math.max(120, current.width + direction[0]),
        height: Math.max(PANEL_HEADER_HEIGHT, current.height + direction[1]),
      };
    } else {
      workspace.panels[id] = {
        mode: 'float',
        x: visible.x + direction[0],
        y: visible.y + direction[1],
        width: visible.width,
        height: visible.height,
      };
    }
    saveAndLayout();
  }

  function installKeyboard(id, registration) {
    const header = registration.element.querySelector('.panel-header');
    if (!header) return;
    header.tabIndex = 0;
    header.setAttribute('role', 'toolbar');
    header.setAttribute(
      'aria-label',
      `${registration.element.getAttribute('aria-label') ?? id} panel. Arrow keys move; Shift plus Arrow keys resize.`,
    );
    header.setAttribute(
      'aria-keyshortcuts',
      'ArrowLeft ArrowRight ArrowUp ArrowDown Shift+ArrowLeft Shift+ArrowRight Shift+ArrowUp Shift+ArrowDown',
    );
    const keydown = (event) => {
      if (!event.key.startsWith('Arrow')) return;
      event.preventDefault();
      keyboardAdjust(id, event.key, event.shiftKey);
    };
    header.addEventListener('keydown', keydown);
    cleanup.push(() => header.removeEventListener('keydown', keydown));
  }

  function installDrag(id, registration) {
    const header = registration.element.querySelector('.panel-header');
    if (!header) return;
    const down = (event) => {
      if (narrow || event.button !== 0 || event.target.closest('[data-panel-close]')) return;
      event.preventDefault();
      raise(id);
      const start = rectOf(registration.element);
      const origin = { x: event.clientX, y: event.clientY };
      let dragged = { ...start };
      let snap = null;
      let moved = false;
      let ended = false;
      header.setPointerCapture(event.pointerId);
      registration.element.dataset.dragging = 'true';

      const move = (moveEvent) => {
        if (moveEvent.pointerId !== event.pointerId) return;
        if (Math.abs(moveEvent.clientX - origin.x) < 2 && Math.abs(moveEvent.clientY - origin.y) < 2) return;
        moved = true;
        dragged = {
          ...start,
          x: start.x + moveEvent.clientX - origin.x,
          y: start.y + moveEvent.clientY - origin.y,
        };
        registration.element.dataset.panelMode = 'float';
        setBox(registration.element, dragged);
        const candidates = [...panels.entries()]
          .filter(([candidateId]) => candidateId !== id && panelState(candidateId)?.mode !== 'closed')
          .map(([candidateId, candidate]) => ({ id: candidateId, ...rectOf(candidate.element) }));
        snap = findSnap(dragged, candidates, usableBounds());
        showPreview(snap, dragged);
      };
      const finish = (endEvent, commit, restore = true) => {
        if (endEvent?.pointerId != null && endEvent.pointerId !== event.pointerId) return;
        if (ended) return;
        ended = true;
        header.removeEventListener('pointermove', move);
        header.removeEventListener('pointerup', up);
        header.removeEventListener('pointercancel', cancel);
        header.removeEventListener('lostpointercapture', lost);
        activeGestures.delete(abort);
        registration.element.dataset.dragging = 'false';
        preview.dataset.open = 'false';
        preview.setAttribute('aria-hidden', 'true');
        try {
          if (header.hasPointerCapture(event.pointerId)) header.releasePointerCapture(event.pointerId);
        } catch {
          // Capture may already have been released by the browser.
        }
        if (commit && moved) {
          commitSnap(id, snap, dragged);
          saveAndLayout();
        } else if (moved && restore) {
          layout();
        }
      };
      const up = (upEvent) => finish(upEvent, true);
      const cancel = (cancelEvent) => finish(cancelEvent, false);
      const lost = (lostEvent) => finish(lostEvent, false);
      const abort = () => finish(null, false, false);
      header.addEventListener('pointermove', move);
      header.addEventListener('pointerup', up);
      header.addEventListener('pointercancel', cancel);
      header.addEventListener('lostpointercapture', lost);
      activeGestures.add(abort);
    };
    header.addEventListener('pointerdown', down);
    cleanup.push(() => header.removeEventListener('pointerdown', down));
  }

  function installResize(id, registration) {
    for (const edge of RESIZE_EDGES) {
      let handle = registration.element.querySelector(`.panel-resize[data-edge="${edge}"]`);
      if (!handle) {
        handle = document.createElement('div');
        handle.className = 'panel-resize';
        handle.dataset.edge = edge;
        handle.tabIndex = 0;
        handle.setAttribute('role', 'separator');
        handle.setAttribute('aria-label', `Resize ${id} panel from ${edge} edge. Use Arrow keys.`);
        registration.element.append(handle);
      }
      const keydown = (event) => {
        if (!event.key.startsWith('Arrow')) return;
        event.preventDefault();
        keyboardAdjust(id, event.key, true);
      };
      handle.addEventListener('keydown', keydown);
      cleanup.push(() => handle.removeEventListener('keydown', keydown));
      const down = (event) => {
        if (narrow || event.button !== 0 || panelState(id)?.mode === 'closed') return;
        event.preventDefault();
        event.stopPropagation();
        const start = rectOf(registration.element);
        const origin = { x: event.clientX, y: event.clientY };
        let ended = false;
        handle.setPointerCapture(event.pointerId);
        const move = (moveEvent) => {
          if (moveEvent.pointerId !== event.pointerId) return;
          const dx = moveEvent.clientX - origin.x;
          const dy = moveEvent.clientY - origin.y;
          let next = { ...start };
          if (edge.includes('e')) next.width = Math.max(120, start.width + dx);
          if (edge.includes('s')) next.height = Math.max(PANEL_HEADER_HEIGHT, start.height + dy);
          if (edge.includes('w')) {
            next.x = start.x + dx;
            next.width = Math.max(120, start.width - dx);
          }
          if (edge.includes('n')) {
            next.y = start.y + dy;
            next.height = Math.max(PANEL_HEADER_HEIGHT, start.height - dy);
          }
          setBox(registration.element, next);
          registration.pendingResize = next;
          viewport?.resize?.();
        };
        const finish = (endEvent, commit, restore = true) => {
          if (endEvent?.pointerId != null && endEvent.pointerId !== event.pointerId) return;
          if (ended) return;
          ended = true;
          handle.removeEventListener('pointermove', move);
          handle.removeEventListener('pointerup', up);
          handle.removeEventListener('pointercancel', cancel);
          handle.removeEventListener('lostpointercapture', lost);
          activeGestures.delete(abort);
          try {
            if (handle.hasPointerCapture(event.pointerId)) handle.releasePointerCapture(event.pointerId);
          } catch {
            // Capture may already have been released by the browser.
          }
          if (commit && registration.pendingResize) {
            const current = panelState(id);
            workspace.panels[id] = current.mode === 'dock'
              ? { ...current, width: registration.pendingResize.width, height: registration.pendingResize.height }
              : { ...current, ...registration.pendingResize };
            registration.pendingResize = null;
            saveAndLayout();
          } else {
            registration.pendingResize = null;
            if (restore) layout();
          }
        };
        const up = (upEvent) => finish(upEvent, true);
        const cancel = (cancelEvent) => finish(cancelEvent, false);
        const lost = (lostEvent) => finish(lostEvent, false);
        const abort = () => finish(null, false, false);
        handle.addEventListener('pointermove', move);
        handle.addEventListener('pointerup', up);
        handle.addEventListener('pointercancel', cancel);
        handle.addEventListener('lostpointercapture', lost);
        activeGestures.add(abort);
      };
      handle.addEventListener('pointerdown', down);
      cleanup.push(() => handle.removeEventListener('pointerdown', down));
    }
  }

  function register(id, element, defaults) {
    if (!element) throw new Error(`panel '${id}' element is required`);
    if (panels.has(id)) throw new Error(`panel '${id}' is already registered`);
    const fallback = defaults ?? DEFAULT_PANELS[id];
    if (!fallback) throw new Error(`panel '${id}' defaults are required`);
    if (!workspace.panels[id]) {
      workspace.panels[id] = normaliseRegisteredPanel(desktopSource?.panels?.[id], fallback, usableBounds());
    }
    const registration = { element, defaults: copy(fallback), pendingResize: null };
    panels.set(id, registration);
    element.setAttribute('role', element.getAttribute('role') ?? 'region');
    element.dataset.panelId = id;
    const raisePanel = () => raise(id);
    element.addEventListener('pointerdown', raisePanel);
    const close = element.querySelector('[data-panel-close]');
    const closePanel = () => toggle(id, false);
    close?.addEventListener('click', closePanel);
    cleanup.push(() => close?.removeEventListener('click', closePanel));
    cleanup.push(() => element.removeEventListener('pointerdown', raisePanel));
    installDrag(id, registration);
    installResize(id, registration);
    installKeyboard(id, registration);
    layout();
    return element;
  }

  function toggle(id, force) {
    if (!panels.has(id)) return false;
    refreshResponsiveState();
    if (narrow) {
      if (id === 'tools') {
        const opening = force ?? !narrowToolsOpen;
        narrowToolsOpen = opening;
        layout();
        return opening;
      }
      const opening = force ?? sheetOpen !== id;
      sheetOpen = opening ? id : null;
      layout();
      return opening;
    }
    const current = panelState(id);
    const opening = force ?? current.mode === 'closed';
    if (opening) {
      if (current.mode !== 'closed') {
        layout();
        return true;
      }
      const restored = current.openMode === 'dock' && current.edge
        ? {
            mode: 'dock', edge: current.edge, lane: current.lane ?? 0,
            order: current.order ?? 0,
            width: current.width, height: current.height,
          }
        : current.openMode === 'float'
          ? {
              mode: 'float', x: current.x, y: current.y,
              width: current.width, height: current.height,
            }
          : copy(lastOpen.get(id) ?? panels.get(id).defaults);
      workspace.panels[id] = restored.mode === 'closed' ? { ...restored, mode: 'float' } : restored;
      if (id === 'add' && workspace.panels[id].mode === 'float') {
        workspace.panels[id].x = pointer.x + 12;
        workspace.panels[id].y = pointer.y;
      }
    } else {
      if (current.mode !== 'closed') {
        lastOpen.set(id, copy(current));
        const visible = rectOf(panels.get(id).element);
        workspace.panels[id] = {
          ...current,
          mode: 'closed',
          openMode: current.mode,
          x: visible.x,
          y: visible.y,
        };
      }
    }
    saveAndLayout();
    return opening;
  }

  function reset() {
    refreshResponsiveState();
    workspace = normaliseWorkspace(null, usableBounds());
    for (const [id, registration] of panels) {
      if (!workspace.panels[id]) {
        workspace.panels[id] = normaliseRegisteredPanel(null, registration.defaults, usableBounds());
      }
    }
    lastOpen.clear();
    sheetOpen = 'outliner';
    narrowToolsOpen = workspace.panels.tools?.mode !== 'closed';
    desktopSource = copy(workspace);
    restoreDesktopOnWide = narrow;
    persist(true);
    layout();
  }

  const resize = () => layout();
  window.addEventListener('resize', resize);
  cleanup.push(() => window.removeEventListener('resize', resize));
  const rememberPointer = (event) => {
    pointer = { x: event.clientX, y: event.clientY };
  };
  root.addEventListener('pointermove', rememberPointer);
  cleanup.push(() => root.removeEventListener('pointermove', rememberPointer));
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => viewport?.resize?.());
  if (frame) observer?.observe(frame);

  function destroy() {
    for (const abort of [...activeGestures]) abort();
    activeGestures.clear();
    for (const { element } of panels.values()) element.dataset.dragging = 'false';
    observer?.disconnect();
    cleanup.splice(0).forEach((dispose) => dispose());
    preview.remove();
  }

  return { register, toggle, reset, layout, destroy };
}
